import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import * as CorgeeHelper from '../utils/CorgeeHelper';
import { nanoid } from 'nanoid';
import ConversationApi from '../ConversationItem/api/ConversationApi';
import ReactSummernoteCustom from './SummernoteCustom';
import {
  extractValidatedPasteHtml,
  changeStyles,
  validateUrls,
  isImageOnlyHtml,
  hoistBlocksOutOfParagraphs,
} from './summernotePasteUtils';
import { enableImageResizeHandles } from './summernote/imageResizeHandles';
import { buildConversationDetailsUrl } from '@lib/conversationUrl';
import './ConversationSummernoteStyles.css';

// Interfaces
interface Layer {
  LayerId: number;
  Name?: string;
  Color?: string;
}

interface Person {
  Name: string;
  IsEnable: boolean;
  ConversationId: number;
  PersonId: number;
}

interface ChatGroup {
  NameGroup: string;
  ParticipantsByID: string;
  ParticipantsByName: string;
}

interface Card {
  ConversationId: number;
  Title: string;
  ProjectName: string;
}

interface TagListData {
  PeopleList: Array<{
    PersonID: number;
    Name: string;
    HasLayerPermissions: boolean;
  }>;
  ChatGroups: ChatGroup[];
}

interface ApiConfig {
  url: string;
  token: string;
}

interface SummernoteCorgeeProps {
  selectorContainer?: string;
  body: string;
  onChangeBody?: (content: string) => void;
  isAddReply?: boolean;
  layerColor?: string;
  textHeight?: string | number;
  projectId?: number;
  configValueId?: number;
  layers: Layer[];
  groupIdsSelected?: string;
  conversationId: number;
  api: ApiConfig;
  isImportant?: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
  onUpdateTemporalImportant?: (important: boolean) => void;
  isInfowindowViewCard?: boolean;
  isInfoWindowChat?: boolean;
  onDropFiles?: (files: File[]) => void;
  fixedDropDownPosition?: boolean;
  children?: React.ReactNode;
}

declare global {
  interface Window {
    clipboardData?: DataTransfer;
  }
}

// Inline images are base64 in the body, so an oversized paste is re-encoded
// before it reaches the DOM. Legacy posted the original file untouched, so the
// ceilings sit high enough that ordinary photos and screenshots (e.g. a
// 2048x1366 JPEG) pass through byte-for-byte; only pathological pastes such as
// huge PureRef boards are re-encoded.
const MAX_INLINE_IMAGE_DIMENSION = 8192;
const MAX_INLINE_IMAGE_BYTES = 6 * 1024 * 1024;
const MAX_LOSSLESS_INLINE_BYTES = 6 * 1024 * 1024;
// Quality is stepped down only until the encode fits the target.
const TARGET_INLINE_IMAGE_BYTES = 4 * 1024 * 1024;
const INLINE_IMAGE_QUALITY_STEPS = [0.95, 0.9, 0.85, 0.8, 0.7];

// WebP is ~30% smaller than JPEG at the same visible quality, and keeps alpha.
let webpSupport: boolean | null = null;
const supportsWebp = (): boolean =>
{
  if (webpSupport === null)
  {
    const probe = document.createElement('canvas');
    probe.width = 1;
    probe.height = 1;
    webpSupport = probe.toDataURL('image/webp').startsWith('data:image/webp');
  }
  return webpSupport;
};

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) =>
  {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result as string));
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(file);
  });

// Resolves to a data URL small enough to keep inline. Images already within
// budget are kept byte-for-byte.
const buildInlineImageSrc = (file: File): Promise<string> =>
{
  const isWithinByteBudget = file.size <= MAX_INLINE_IMAGE_BYTES;

  // A canvas re-encode would drop every GIF frame but the first.
  if (file.type === 'image/gif' || file.type === 'image/svg+xml')
  {
    return readFileAsDataUrl(file);
  }

  return new Promise((resolve, reject) =>
  {
    // Object URL keeps the original off the JS heap.
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.addEventListener('load', () =>
    {
      try
      {
        const { naturalWidth: width, naturalHeight: height } = img;
        const scale = Math.min(1, MAX_INLINE_IMAGE_DIMENSION / Math.max(width, height));

        if (scale === 1 && isWithinByteBudget)
        {
          resolve(readFileAsDataUrl(file));
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(width * scale));
        canvas.height = Math.max(1, Math.round(height * scale));

        const ctx = canvas.getContext('2d');
        if (!ctx)
        {
          resolve(readFileAsDataUrl(file));
          return;
        }

        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // Lossless first; a lossy encode only when the PNG is too heavy to carry.
        const losslessSrc = canvas.toDataURL('image/png');
        if (losslessSrc.length <= MAX_LOSSLESS_INLINE_BYTES)
        {
          resolve(losslessSrc);
          return;
        }

        const mimeType = supportsWebp() ? 'image/webp' : 'image/jpeg';

        // JPEG has no alpha, so paint white behind the image to avoid a black
        // background on transparent sources. WebP keeps alpha.
        if (mimeType === 'image/jpeg')
        {
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        let encoded = '';
        for (const quality of INLINE_IMAGE_QUALITY_STEPS)
        {
          encoded = canvas.toDataURL(mimeType, quality);
          if (encoded.length <= TARGET_INLINE_IMAGE_BYTES) break;
        }

        resolve(encoded);
      }
      catch (error)
      {
        reject(error);
      }
      finally
      {
        URL.revokeObjectURL(objectUrl);
      }
    });

    img.addEventListener('error', () =>
    {
      URL.revokeObjectURL(objectUrl);
      // Not decodable here - embed it untouched.
      resolve(readFileAsDataUrl(file));
    });

    img.src = objectUrl;
  });
};

// A pasted screenshot is normally wider than the editor, so at natural size one
// image fills the whole row and the next one wraps to the next line. Inserting
// it small instead lets several images share a line. The resize handles
// overwrite these inline dimensions afterwards.
//
// The box matches the thumbnail the API generates for embedded images (fit
// within 250x230) and swaps into the body's src when the post is saved. A
// larger inline width would stretch that thumbnail and blur it, which legacy
// never did because it showed the thumbnail at its own size.
const MAX_PASTED_IMAGE_DISPLAY_WIDTH = 250;
const MAX_PASTED_IMAGE_DISPLAY_HEIGHT = 230;

const applyPastedImageDisplaySize = (
  node: HTMLImageElement,
  editableEl?: HTMLElement | null,
): void =>
{
  const { naturalWidth, naturalHeight } = node;
  if (!naturalWidth || !naturalHeight) return;

  const maxWidth = editableEl?.clientWidth || naturalWidth;
  const scale = Math.min(
    1,
    MAX_PASTED_IMAGE_DISPLAY_WIDTH / naturalWidth,
    MAX_PASTED_IMAGE_DISPLAY_HEIGHT / naturalHeight,
    maxWidth / naturalWidth,
  );

  node.style.width = `${Math.round(naturalWidth * scale)}px`;
  node.style.height = 'auto';
};

export interface SummernoteCorgeeRef {
  getPeopleTagList: (enterGetTagList?: boolean) => Promise<void>;
  setPossibleTagList: (listPeopleNameParam?: Person[]) => number[];
  clearContent: () => void;
  getContent: () => string | null;
}

const SummernoteCorgee = forwardRef<SummernoteCorgeeRef, SummernoteCorgeeProps>(({
  selectorContainer = '.card-content-container',
  body: initialBody,
  onChangeBody,
  isAddReply = false,
  layerColor = 'transparent',
  textHeight,
  projectId,
  configValueId,
  layers,
  groupIdsSelected,
  conversationId,
  api,
  isImportant: initialIsImportant = false,
  onKeyDown: onKeyDownProp,
  onUpdateTemporalImportant,
  isInfowindowViewCard,
  isInfoWindowChat,
  onDropFiles,
  fixedDropDownPosition: _fixedDropDownPosition = true,
  children,
}, ref) =>
{
  const corgeeSummernoteRef = useRef<any>(null);
  const isMountedRef = useRef<boolean>(false);
  const isFirstLoadRef = useRef<boolean>(true);
  // Guards against overlapping, concurrent getTagList calls for the identical
  // request - the effect's own prevLayerIdsRef guard only prevents later,
  // separate re-runs from re-fetching, not two invocations that are both
  // already in flight before either resolves (e.g. React StrictMode's
  // dev-only double-invoke of the effect that calls this).
  const inFlightTagListKeyRef = useRef<string | null>(null);
  const onDropFilesRef = useRef(onDropFiles);
  // Kept in sync after onImageUploadFix is defined; used by the drop handler
  // (which lives in a mount-only effect) to embed dropped images inline.
  const onImageUploadFixRef = useRef<((files: File[] | FileList) => void) | null>(null);
  // Debounced: each pass re-serializes the whole editor.
  const postProcessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const peopleTagsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statePushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isImportant, setIsImportant] = useState<boolean>(initialIsImportant);
  const [body, setBody] = useState<string>(initialBody);
  const [listPeopleName, setListPeopleName] = useState<Person[]>([]);
  const [listPeopleIds, setListPeopleIds] = useState<number[]>([]);
  const [groupName, setGroupName] = useState<ChatGroup[]>([]);
  const [_dataTagList, setDataTagList] = useState<TagListData | null>(null);
  const getPeopleMentionsRef = useRef<() => Person[]>(() => []);
  const getCardsLinkedRef = useRef<() => Card[]>(() => []);
  const groupNameRef = useRef<ChatGroup[]>([]);

  const toolbar = useMemo(
    () => [
      ['style', ['bold', 'italic', 'underline']],
      ['fontface', ['fontname']],
      ['textsize', ['fontsize']],
      ['fontclr', ['color']],
      ['alignment', ['ul', 'ol', 'paragraph', 'lineheight']],
      ['insert', ['link', 'picture']],
      ['customOptions', ['AddCheckList', 'AddWarningMessage']],
    ],
    [],
  );

  const urlRegex = useMemo(() => /(https?:\/\/[^\s]+)/g, []);

  const updateColorLayerSelected = useCallback(
    (element?: JQuery, color?: string) =>
    {
      const colorToUse = color ?? layerColor;
      if (!colorToUse || colorToUse === 'transparent') return;

      let el = element;

      if (!el && corgeeSummernoteRef.current)
      {
        el = $(corgeeSummernoteRef.current.editor).closest(selectorContainer);
      }

      if (el && el.length > 0)
      {
        const noteEditor = el.find('.note-editor.note-frame');
        if (noteEditor.length > 0)
        {
          noteEditor.css({ 'border-color': colorToUse });
        }

        const noteToolbar = el.find('.note-toolbar');
        if (noteToolbar.length > 0)
        {
          noteToolbar.css({ 'background-color': colorToUse });
        }

        el.find('.note-statusbar').css({ 'background-color': colorToUse });
        el.find('.note-status-output').css({ 'background-color': colorToUse });
        el.find('.note-resizebar').css({ 'background-color': colorToUse });

        const modalw = $(corgeeSummernoteRef.current?.editor).closest('.ui-dialog');
        if (modalw.length > 0)
        {
          modalw.css('height', '');
        }
      }
    },
    [layerColor, selectorContainer],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) =>
    {
      if (onKeyDownProp)
      {
        setTimeout(() =>
        {
          onKeyDownProp(e);
        }, 250);
      }
    },
    [onKeyDownProp],
  );

  const setPossibleTagList = useCallback(
    (listPeopleNameParam?: Person[]) =>
    {
      const peopleList = listPeopleNameParam ?? listPeopleName;
      const peopleIds = peopleList.map(item => item.PersonId).filter(id => typeof id === 'number');

      setListPeopleIds(peopleIds);
      return peopleIds;
    },
    [listPeopleName],
  );

  const getPeopleTagList = useCallback(
    async (enterGetTagList: boolean = true) =>
    {
      const requestParams = {
        ConversationId: conversationId,
        ProjectId: projectId,
        ConfigValueId: configValueId,
        LayerIdsSelected: layers.map(layer => layer.LayerId).join(','),
        GroupIdsSelected: groupIdsSelected ?? '',
      };

      if (layers.length === 0)
      {
        setListPeopleName([]);
        setGroupName([]);
        setDataTagList(null);
        setPossibleTagList([]);
        return;
      }

      if (!isFirstLoadRef.current)
      {
        isFirstLoadRef.current = true;
      }
      else
      {
        isFirstLoadRef.current = false;
      }

      if (enterGetTagList)
      {
        const requestKey =
          `${requestParams.ConversationId}|${requestParams.ProjectId}|` +
          `${requestParams.LayerIdsSelected}|${requestParams.GroupIdsSelected}`;

        if (inFlightTagListKeyRef.current === requestKey)
        {
          return;
        }
        inFlightTagListKeyRef.current = requestKey;

        try
        {
          const data = await ConversationApi.getTagList(api, requestParams);

          if (!isMountedRef.current) return;

          const peopleName: Person[] = [];

          data.PeopleList.forEach((person: any) =>
          {
            if (person.PersonID !== null)
            {
              peopleName.push({
                Name: `(${person.PersonID}) ${person.Name}`,
                IsEnable: person.HasLayerPermissions,
                ConversationId: conversationId,
                PersonId: person.PersonID,
              });
            }
          });

          peopleName.sort((x, y) => Number(y.IsEnable) - Number(x.IsEnable));

          data.ChatGroups.forEach((group: any) =>
          {
            if (group.ParticipantsByID !== null)
            {
              peopleName.push({
                Name: group.NameGroup,
                IsEnable: true,
                ConversationId: conversationId,
                PersonId: parseInt(group.ParticipantsByID),
              });
            }
          });

          setListPeopleName(peopleName);
          setGroupName(data.ChatGroups);
          setDataTagList(data);
          setPossibleTagList(peopleName);
          checkValidPeopleTags();
        }
        catch (error)
        {
          console.error('Error fetching tag list:', error);
        }
        finally
        {
          if (inFlightTagListKeyRef.current === requestKey)
          {
            inFlightTagListKeyRef.current = null;
          }
        }
      }
    },
    [api, conversationId, projectId, configValueId, layers, groupIdsSelected, setPossibleTagList],
  );

  const clearContent = useCallback(() =>
  {
    if (corgeeSummernoteRef.current)
    {
      corgeeSummernoteRef.current.summernote('code', '');
    }
    setBody('');
    onChangeBody?.('');
  }, [onChangeBody]);

  // The editable DOM is the source of truth; callers read it at save time.
  const getContent = useCallback((): string | null =>
  {
    const editableEl = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;
    if (!editableEl) return null;

    // Saved HTML must survive a re-parse unchanged, or the posted reply differs from the editor
    hoistBlocksOutOfParagraphs(editableEl);
    return editableEl.innerHTML;
  }, []);

  useImperativeHandle(ref, () => ({ getPeopleTagList, setPossibleTagList, clearContent, getContent }), [getPeopleTagList, setPossibleTagList, clearContent, getContent]);

  const prevLayerIdsRef = useRef<string>('');
  const prevProjectIdRef = useRef<number | undefined>(undefined);

  useEffect(() =>
  {
    // Reset layer tracking when project changes so the next layer set always triggers a fresh call
    if (projectId !== prevProjectIdRef.current)
    {
      prevProjectIdRef.current = projectId;
      prevLayerIdsRef.current = '';
    }

    const ids = layers.map(l => l.LayerId).join(',');
    if (ids && ids !== prevLayerIdsRef.current)
    {
      prevLayerIdsRef.current = ids;
      getPeopleTagList(true);
    }
  }, [layers, projectId, getPeopleTagList]);

  const getPeopleMentionsOnBody = useCallback((): Person[] =>
  {
    const peopleNameAvailable = [...listPeopleName];
    peopleNameAvailable.sort((x, y) => Number(y.IsEnable) - Number(x.IsEnable));

    const htmlBody = `<div>${body}</div>`;
    const taggedPeople = $(htmlBody).find('span[person-id]');

    if (layers.length === 0)
    {
      taggedPeople.remove();
      return [];
    }

    taggedPeople.each((_, tag) =>
    {
      const valuePersonId = $(tag).attr('person-id');
      const personId = parseInt(valuePersonId ?? '0');

      const indexMention = peopleNameAvailable.findIndex(person => person.PersonId === personId);

      if (indexMention >= 0)
      {
        peopleNameAvailable.splice(indexMention, 1);
      }
    });

    return peopleNameAvailable;
  }, [body, listPeopleName, layers]);

  const getCardsLinkedOnBody = useCallback((): Card[] =>
  {
    const cardsRecentsCache = localStorage.getItem('C2_objStorageConversation');

    if (!cardsRecentsCache)
    {
      localStorage.setItem('C2_objStorageConversation', JSON.stringify([]));
      return [];
    }

    const cardsRecents = JSON.parse(cardsRecentsCache);
    const data = cardsRecents.filter((item: Card) => item.ConversationId !== conversationId);

    const cardsAvailable = [...data];
    const htmlBody = `<div>${body}</div>`;
    const linkedCards = $(htmlBody).find('a[link-conversation-id]');

    linkedCards.each((_, link) =>
    {
      const valueConversationId = $(link).attr('link-conversation-id');
      const convId = parseInt(valueConversationId ?? '0');

      const indexMention = cardsAvailable.findIndex(card => card.ConversationId === convId);

      if (indexMention >= 0)
      {
        cardsAvailable.splice(indexMention, 1);
      }
    });

    return cardsAvailable;
  }, [body, conversationId]);

  const urlify = useCallback(
    (text: string): string =>
    {
      return text.replace(urlRegex, url =>
      {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      });
    },
    [urlRegex],
  );
  void urlify;

  const checkValidPeopleTags = useCallback(() =>
  {
    try
    {
      if (listPeopleIds.length === 0) return;

      // Cheap guard: the parse below builds a real DOM, decoding any inline image.
      if (!body.includes('person-id')) return;

      /*
       * Wrapped before parsing, like getPeopleMentionsOnBody and getCardsLinkedOnBody do.
       * $(body) on a string with no leading '<' (e.g. 'dw<span></span>', which is what the
       * editor produces when a reply has a file but no text) is sent to Sizzle as a selector
       * and throws. Wrapping also makes .find() see a top-level span[person-id], which it
       * would otherwise miss since find() only searches descendants.
       */
      const htmlBody = `<div>${body}</div>`;

      let bodyTags = $(htmlBody)
        .find('span[person-id]')
        .map(function ()
        {
          return parseInt($(this).attr('person-id') ?? '0');
        })
        .get();

      if (bodyTags.length === 0) return;

      bodyTags = Array.from(new Set(bodyTags));

      const eraseTags = bodyTags.filter(tag => !listPeopleIds.includes(tag));

      if (eraseTags.length > 0)
      {
        let content = `<div>${body}</div>`;

        eraseTags.forEach(tagId =>
        {
          content = $(content).find(`span[person-id='${tagId}']`).remove().end()[0]!.outerHTML;
        });

        setBody(content);
        onChangeBody?.(content);
      }
    }
    catch (error)
    {
      console.error('Error checking valid people tags:', error);
    }
  }, [body, listPeopleIds, onChangeBody]);

  const updateBodyByDOM = useCallback(() =>
  {
    if (corgeeSummernoteRef.current)
    {
      const element = $(corgeeSummernoteRef.current.editor).closest(selectorContainer);
      const elementNoteEditable = element.find('.note-editor').find('.note-editable');
      onChangeSummernoteInternal(elementNoteEditable.html());
    }
  }, [selectorContainer]);

  const normalizeStructuralListWrappers = useCallback((editableEl: HTMLElement) =>
  {
    editableEl.querySelectorAll('li').forEach(li =>
    {
      const isPureListWrapper =
        li.childNodes.length === 1 &&
        li.children.length === 1 &&
        /^(UL|OL)$/.test(li.children[0]!.tagName);

      if (isPureListWrapper && li.parentNode)
      {
        li.parentNode.replaceChild(li.children[0]!, li);
      }
    });
  }, []);

  // Adjusts padding-left on each <ul>/<ol> based on the largest font-size found
  // in that list. 55px when any span >= 24px (so the small default marker does not
  // look cramped next to large text), CSS default (2em) otherwise.
  // Marker size itself is never changed -- it always inherits the editor default.
  const adjustListPadding = useCallback((editableEl: HTMLElement) =>
  {
    editableEl.querySelectorAll<HTMLElement>('ul, ol').forEach(list =>
    {
      const markerSize = parseFloat(list.style.getPropertyValue('--marker-font-size'));
      if (!isNaN(markerSize) && markerSize >= 24)
        list.style.setProperty('--list-padding', '55px');
      else
        list.style.removeProperty('--list-padding');
    });
  }, []);

  // Editor keeps checklist inputs enabled; the emitted body keeps them disabled so
  // the read-only card view is not toggleable.
  const disableChecklistInHtml = useCallback((html: string) =>
  {
    if (!html || !html.includes('note-editor-checkbox')) return html;

    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('input.note-editor-checkbox').forEach(cb =>
    {
      cb.setAttribute('disabled', 'true');
    });
    return div.innerHTML;
  }, []);

  const onChangeSummernoteInternal = useCallback(
    (content: string) =>
    {
      let normalizedContent = content;
      if (corgeeSummernoteRef.current?.noteEditable)
      {
        normalizeStructuralListWrappers(corgeeSummernoteRef.current.noteEditable[0]);
        hoistBlocksOutOfParagraphs(corgeeSummernoteRef.current.noteEditable[0]);
        normalizedContent = corgeeSummernoteRef.current.noteEditable[0].innerHTML;
      }
      normalizedContent = disableChecklistInHtml(normalizedContent);

      if (postProcessTimerRef.current) clearTimeout(postProcessTimerRef.current);
      postProcessTimerRef.current = setTimeout(() =>
      {
        if (corgeeSummernoteRef.current?.noteEditable)
        {
          let updatedContent = corgeeSummernoteRef.current.noteEditable[0].innerHTML;

          // Skip the link fixups when there is no link or URL to fix.
          if (!updatedContent.includes('<a') && !updatedContent.includes('http'))
          {
            const plainContent = disableChecklistInHtml(updatedContent);
            if (plainContent === body) return;
            setBody(plainContent);
            onChangeBody?.(plainContent);
            return;
          }

          // Fix Drag and Drop links from teams
          updatedContent = updatedContent.replace(
            /(<p>)(https?:\/\/[^\s<]+)(https?:\/\/[^\s<]+)?\s*<a [^>]+>(https?:\/\/[^\s<]+)<\/a>/gi,
            (match: any, pTag: any, url1: any, url2: any, urlInLink: any) =>
            {
              if (url1 === urlInLink || url2 === urlInLink)
              {
                return `${pTag}<a href="${urlInLink}" target="_blank">${urlInLink}</a>`;
              }
              return match;
            },
          );

          // Remove duplicates
          updatedContent = updatedContent.replace(/(https?:\/\/[^\s<]+)\1/g, '$1');

          // Force target="_blank" on all links that don't already have it
          updatedContent = updatedContent.replace(/<a(?![^>]*\btarget=)([^>]*)>/gi, '<a target="_blank"$1>');

          // Convert plain URL to link
          const onlyUrl = updatedContent.match(/^<p>(https?:\/\/[^\s<]+)<br><\/p>$/i);
          if (onlyUrl)
          {
            const url = onlyUrl[1];
            updatedContent = `<p><a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a><br></p>`;
          }

          updatedContent = disableChecklistInHtml(updatedContent);

          if (updatedContent === body) return;

          setBody(updatedContent);
          onChangeBody?.(updatedContent);
        }
      }, 500);

      // Debounced so continuous typing does not re-render the host on every key.
      if (statePushTimerRef.current) clearTimeout(statePushTimerRef.current);
      statePushTimerRef.current = setTimeout(() =>
      {
        setBody(normalizedContent);
        onChangeBody?.(normalizedContent);
      }, 150);

      if (peopleTagsTimerRef.current) clearTimeout(peopleTagsTimerRef.current);
      peopleTagsTimerRef.current = setTimeout(() =>
      {
        checkValidPeopleTags();
      }, 500);
    },
    [body, onChangeBody, checkValidPeopleTags, normalizeStructuralListWrappers, disableChecklistInHtml],
  );

  // Bound to blur so a click on a host's Save button lands after this.
  const flushBodyToHost = useCallback(() =>
  {
    if (statePushTimerRef.current)
    {
      clearTimeout(statePushTimerRef.current);
      statePushTimerRef.current = null;
    }

    const editableEl = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;
    if (!editableEl) return;

    hoistBlocksOutOfParagraphs(editableEl);
    const content = disableChecklistInHtml(editableEl.innerHTML);
    setBody(content);
    onChangeBody?.(content);
  }, [onChangeBody, disableChecklistInHtml]);

  const onInit = useCallback(
    (_params: any) =>
    {
      if (isInfowindowViewCard && isAddReply)
      {
        const cardBody = $('.corgee_com_card-body.corgee_com_card-body-info');
        cardBody.scrollTop((cardBody.scrollTop() ?? 0) + (cardBody.height() ?? 0));
      }

      if (!isInfowindowViewCard && isAddReply)
      {
        const htmlScroll = $('html').scrollTop() ?? 0;
        const containerHeight = $('.corgee_com_card-add-replay-container').height() ?? 0;
        $('html').scrollTop(htmlScroll + containerHeight);

        const bodyScroll = $('body').scrollTop() ?? 0;
        $('body').scrollTop(bodyScroll + containerHeight);
      }

      if (corgeeSummernoteRef.current)
      {
        const element = $(corgeeSummernoteRef.current.noteEditable);
        const noteContainer = element;

        if (noteContainer.length > 0)
        {
          noteContainer.scroll(() =>
          {
            $(document).find('.note-link-popover').css('display', 'none');
          });
        }

        const infoWindowContainer = element.closest('.card-body-area');
        if (infoWindowContainer.length > 0)
        {
          infoWindowContainer.scroll(() =>
          {
            $(document).find('.note-link-popover').css('display', 'none');
          });
        }
      }
    },
    [isInfowindowViewCard, isAddReply],
  );

  // Captured synchronously so an insert can happen after an async gap
  // (network/file read) without depending on the live browser selection.
  const captureEditableRange = useCallback((): Range | null =>
  {
    const editableEl = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;
    if (!editableEl) return null;

    editableEl.focus();

    const selection = window.getSelection();
    const hasSelectionInside =
      selection && selection.rangeCount > 0 && editableEl.contains(selection.getRangeAt(0).startContainer);

    if (hasSelectionInside)
    {
      return selection!.getRangeAt(0).cloneRange();
    }

    const range = document.createRange();
    range.selectNodeContents(editableEl);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
    return range;
  }, []);

  const onPaste = useCallback(
    async (e: any) =>
    {
      const clipboardData =
        e.clipboardData || e.originalEvent?.clipboardData || window.clipboardData;
      const clipboardText = clipboardData?.getData('text/plain') ?? '';

      if (clipboardText.includes('Dashboard/FileImage/'))
      {
        e.preventDefault();
        const ImageId = clipboardText.substr(clipboardText.lastIndexOf('/') + 1);
        const params = { id: ImageId, isReply: false };
        const capturedRange = captureEditableRange();

        const insertFetchedImage = (url: string) =>
        {
          const node = document.createElement('img');
          node.id = nanoid();
          node.src = url;

          const editableEl = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;

          node.addEventListener(
            'load',
            () => applyPastedImageDisplaySize(node, editableEl),
            { once: true },
          );

          if (capturedRange && editableEl?.contains(capturedRange.startContainer))
          {
            capturedRange.deleteContents();
            capturedRange.insertNode(node);
            capturedRange.setStartAfter(node);
            capturedRange.collapse(true);

            const selection = window.getSelection();
            selection?.removeAllRanges();
            selection?.addRange(capturedRange);
          }
          else if (editableEl)
          {
            editableEl.appendChild(node);
          }
        };

        try
        {
          const data = await ConversationApi.GetFileImages(api, params);

          if (data && data.length > 0)
          {
            insertFetchedImage(data[0].FileUrl);
          }
          else if (data && data.length === 0)
          {
            const chatData = await ConversationApi.GetChatFileImages(api, params);
            insertFetchedImage(chatData.FileUrl);
          }
        }
        catch (error)
        {
          console.error('Error pasting image:', error);
        }
        return;
      }

      // Pure image paste: Summernote uploads the clipboard file itself, inserting
      // the HTML payload too would duplicate the image
      const hasPastedImageFile = Array.from<File>(clipboardData?.files ?? []).some(file =>
        file.type.startsWith('image/'),
      );

      if (hasPastedImageFile && isImageOnlyHtml(clipboardData?.getData('text/html') ?? ''))
      {
        return;
      }

      if (CorgeeHelper.isSafari())
      {
        const bufferText = clipboardData?.getData('Text') ?? '';
        e.preventDefault();

        if (e.originalEvent?.clipboardData?.types?.indexOf('image/tiff') !== -1)
        {
          const htmlElement =
            '<div class="clipboard-notification-r ng-scope" id="paste-image" style="display: block;z-index: 99999;">Use another method to add images</div>';
          $('body').prepend(htmlElement);
          $('#paste-image').fadeIn().delay(700).fadeOut(300);
          return;
        }

        // Same cleanup and block-level insertion as the other browsers
        const validatedHtml = extractValidatedPasteHtml(e);
        const text = clipboardData?.getData('text/html') ?? '';
        if (validatedHtml !== null)
        {
          captureEditableRange();
          changeStyles(validatedHtml);
        }
        else if (text !== '')
        {
          document.execCommand('insertHTML', false, text);
        }
        else
        {
          document.execCommand('insertHTML', false, bufferText);
        }
      }
      else
      {
        const index = clipboardData?.types?.indexOf('Files') ?? -1;
        if (index !== -1 && !clipboardData?.files[0])
        {
          const htmlElement =
            '<div class="clipboard-notification-r ng-scope" id="paste-image" style="display: block;z-index: 99999; position:fixed; top:70px;">Use another method to add images</div>';
          $('body').prepend(htmlElement);
          $('#paste-image').fadeIn().delay(1000).fadeOut(300);
          setTimeout(() => $('#paste-image').remove(), 1200);
          return;
        }

        const validatedHtml = extractValidatedPasteHtml(e);
        if (validatedHtml !== null)
        {
          e.stopPropagation();
          e.preventDefault();
          // Ensure a selection exists so the insert below doesn't no-op on an empty editable
          captureEditableRange();
          changeStyles(validatedHtml);
        }
      }
    },
    [api, captureEditableRange],
  );

  const onButtonAddCheckList = useCallback((context: any) =>
  {
    const ui = ($ as any).summernote.ui;

    const button = ui.button({
      contents: '<i class="fa fa-check-square-o"/>',
      click: () =>
      {
        const cbIdAux = `cb_${nanoid()}`;
        context.invoke(
          'editor.pasteHTML',
          `<span contentEditable="false"><input id="${cbIdAux}" type="checkbox" class="note-editor-checkbox"/></span>&nbsp;`,
        );
      },
    });

    const $btn = button.render();
    $btn.attr('title', 'Insert a Checklist');
    return $btn;
  }, []);

  const onButtonAddWarningMessage = useCallback(
    (_context: any) =>
    {
      const ui = ($ as any).summernote.ui;

      const button = ui.button({
        className: isImportant
          ? 'btn-important-mark btn-infowindow-important'
          : 'btn-important-unmark btn-infowindow-important',
        contents: '<i class="material-icons size-icon-important">priority_high</i>',
        click: function ()
        {
          // Read current state from DOM to avoid stale closure
          const currentlyMarked = $(this).hasClass('btn-important-mark');
          const newImportant = !currentlyMarked;

          if (newImportant)
          {
            $(this).removeClass('btn-important-unmark');
            $(this).addClass('btn-important-mark');
            $(this).attr('title', 'Unmark as Important');
          }
          else
          {
            $(this).removeClass('btn-important-mark');
            $(this).addClass('btn-important-unmark');
            $(this).attr('title', 'Mark as Important');
          }

          setIsImportant(newImportant);
          onUpdateTemporalImportant?.(newImportant);
        },
      });

      const $btn = button.render();
      $btn.attr('title', isImportant ? 'Unmark as Important' : 'Mark as Important');
      return $btn;
    },
    [isImportant, onUpdateTemporalImportant],
  );

  const onImageUploadFix = useCallback(
    (files: File[] | FileList) =>
    {
      // Summernote hands over a FileList on paste/drop, not an array
      const uploadedFiles = Array.from(files ?? []);

      if (uploadedFiles[0]?.type?.includes('video'))
      {
        onDropFiles?.(uploadedFiles);
        return;
      }

      if (corgeeSummernoteRef.current)
      {
        const insertions = uploadedFiles.map(file =>
        {
          const capturedRange = captureEditableRange();

          return buildInlineImageSrc(file)
            .then(async src =>
            {
              const node = document.createElement('img');
              node.id = nanoid();
              node.alt = file.name;
              node.src = src;

              const editableEl = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;

              // Decoded before the insert so the display size is already part of
              // the HTML the body sync below reads.
              try
              {
                await node.decode();
              }
              catch
              {
                // Undecodable source - it still inserts, just at natural size
              }
              applyPastedImageDisplaySize(node, editableEl);

              if (capturedRange && editableEl?.contains(capturedRange.startContainer))
              {
                capturedRange.deleteContents();
                capturedRange.insertNode(node);
                capturedRange.setStartAfter(node);
                capturedRange.collapse(true);

                const selection = window.getSelection();
                selection?.removeAllRanges();
                selection?.addRange(capturedRange);
              }
              else if (editableEl)
              {
                editableEl.appendChild(node);
              }
            })
            .catch(error => console.error('Could not embed the pasted image:', error));
        });

        // Sync once every insertion has landed, so no late decode is missed.
        void Promise.all(insertions).then(() =>
        {
          if (corgeeSummernoteRef.current?.noteEditable)
          {
            const content = corgeeSummernoteRef.current.noteEditable[0].innerHTML;
            setBody(content);
            onChangeBody?.(content);
          }
        });
      }
    },
    [onChangeBody, onDropFiles, captureEditableRange],
  );
  onImageUploadFixRef.current = onImageUploadFix;

  // Effects
  useEffect(() =>
  {
    isMountedRef.current = true;
    return () =>
    {
      isMountedRef.current = false;
      if (postProcessTimerRef.current) clearTimeout(postProcessTimerRef.current);
      if (peopleTagsTimerRef.current) clearTimeout(peopleTagsTimerRef.current);
      if (statePushTimerRef.current) clearTimeout(statePushTimerRef.current);
    };
  }, []);

  useEffect(() =>
  {
    onDropFilesRef.current = onDropFiles;
  }, [onDropFiles]);

  useEffect(() =>
  {
    getPeopleMentionsRef.current = getPeopleMentionsOnBody;
  }, [getPeopleMentionsOnBody]);

  useEffect(() =>
  {
    getCardsLinkedRef.current = getCardsLinkedOnBody;
  }, [getCardsLinkedOnBody]);

  useEffect(() =>
  {
    groupNameRef.current = groupName;
  }, [groupName]);

  useEffect(() =>
  {
    if (layerColor && layerColor !== 'transparent')
    {
      updateColorLayerSelected(undefined, layerColor);
    }
  }, [layerColor, updateColorLayerSelected]);

  useEffect(() =>
  {
    if (!corgeeSummernoteRef.current) return;

    const element = $(corgeeSummernoteRef.current.editor).closest(selectorContainer);

    setTimeout(() =>
    {
      element.find('.note-editable').focus();
    }, 50);

    // Checklist inputs: `disabled` is a boolean attribute, so it must be removed to
    // be clickable. Change is delegated so inputs added later are covered too.
    const editableForCheckboxes = element.find('.note-editable');
    editableForCheckboxes.find('.note-editor-checkbox').removeAttr('disabled');

    const onCheckboxChange = function (this: HTMLInputElement)
    {
      if (this.checked)
      {
        $(this).attr('checked', 'checked');
      }
      else
      {
        $(this).removeAttr('checked');
      }
      updateBodyByDOM();
    };
    editableForCheckboxes.on('change', '.note-editor-checkbox', onCheckboxChange);

    if (layerColor && layerColor !== 'transparent')
    {
      updateColorLayerSelected(element, layerColor);
    }

    if (textHeight)
    {
      element.find('.note-editable').css({
        height: `${textHeight}px`,
        'min-height': '170px',
      });
    }

    getPeopleTagList(true);
    updateBodyByDOM();

    // Intercept external text/link drops in capture phase before Summernote's
    // dropzone handler, which calls pasteHTML once per dataTransfer type
    // (text/plain, text/html, text/uri-list all pass its 'text' filter → triple insert).
    // File drops and internal drags are left for Summernote to handle normally.
    const noteEditorEl = element.find('.note-editor')[0];
    let internalDrag = false;

    const onDragStart = () =>
    {
      internalDrag = true;
    };
    const onDragEnd = () =>
    {
      internalDrag = false;
    };

    element.find('.note-editable').on('dragstart', onDragStart);
    element.find('.note-editable').on('dragend', onDragEnd);

    const handleDropCapture = (e: DragEvent) =>
    {
      const dt = e.dataTransfer;

      // When files are dropped, embed images inline in the editor and send any
      // other file type to the FileUpload handler (attachments area).
      if (dt && dt.files.length > 0 && !internalDrag)
      {
        const allFiles = Array.from(dt.files);
        const imageFiles = allFiles.filter(file => file.type.startsWith('image/'));
        const otherFiles = allFiles.filter(file => !file.type.startsWith('image/'));

        e.stopPropagation();
        e.preventDefault();
        // Remove Summernote's drag overlay (it adds 'dragover' class on dragenter
        // and only removes it in its own drop handler, which we've bypassed).
        (e.currentTarget as HTMLElement)?.classList.remove('dragover');

        if (imageFiles.length > 0)
        {
          // Drop the caret at the drop point so the image lands where it was released
          const dropRange = document.caretRangeFromPoint?.(e.clientX, e.clientY);
          const editableEl = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;
          if (dropRange && editableEl?.contains(dropRange.startContainer))
          {
            const sel = window.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(dropRange);
          }
          onImageUploadFixRef.current?.(imageFiles);
        }

        if (otherFiles.length > 0 && onDropFilesRef.current)
        {
          onDropFilesRef.current(otherFiles);
        }

        internalDrag = false;
        return;
      }

      // Let internal editor drags and non-file drops fall through
      if (!dt || dt.files.length > 0 || internalDrag)
      {
        internalDrag = false;
        return;
      }

      e.stopPropagation(); // stop Summernote's multi-type loop
      e.preventDefault(); // stop native contenteditable insertion
      internalDrag = false;

      // Pick the single best type: HTML > plain text
      const html = dt.getData('text/html');
      const plain = dt.getData('text/plain').trim();

      let content: string;
      if (html)
      {
        // Clean it the same way onPaste does before inserting.
        const $html = $('<body />', { html });
        const bodyContent = $($html[0]!.innerHTML);
        bodyContent.find('*').removeAttr('class');
        bodyContent.find('*').removeAttr('onclick');
        validateUrls(bodyContent);

        const tempWrapper = document.createElement('div');
        bodyContent.each((_: number, el: HTMLElement) => { tempWrapper.appendChild(el.cloneNode(true)); });
        content = tempWrapper.innerHTML || html;
      }
      else if (/^https?:\/\/\S+$/.test(plain))
      {
        content = `<a href="${plain}" target="_blank" rel="noopener noreferrer">${plain}</a>`;
      }
      else
      {
        content = plain;
      }

      if (content && corgeeSummernoteRef.current)
      {
        corgeeSummernoteRef.current.summernote('pasteHTML', content);
      }

      document.dispatchEvent(new DragEvent('drop', { bubbles: true, cancelable: true }));
    };

    if (noteEditorEl)
    {
      noteEditorEl.addEventListener('drop', handleDropCapture, true);
    }

    // Safari specific handlers
    if (CorgeeHelper.isSafari())
    {
      element.find('.note-dropzone').on('drop', e =>
      {
        e.preventDefault();
        if (e.originalEvent?.dataTransfer?.types?.indexOf('image/tiff') !== -1)
        {
          const htmlElement =
            '<div class="clipboard-notification-r ng-scope" id="paste-image" style="display: block;z-index: 99999;">Use another method to add images</div>';
          $('body').prepend(htmlElement);
          $('#paste-image').fadeIn().delay(900).fadeOut(300);
          setTimeout(() => $('#paste-image').remove(), 1200);
          return;
        }
      });

      element.find('.note-editing-area').on('dragenter', e =>
      {
        e.preventDefault();
        if (e.originalEvent?.dataTransfer?.types?.indexOf('Files') !== -1)
        {
          if ($('#paste-image').length === 0)
          {
            const htmlElement =
              '<div class="clipboard-notification-r ng-scope" id="paste-image" style="display: block;z-index: 99999;">Try to drop the image in the attachments area</div>';
            $('body').prepend(htmlElement);
            $('#paste-image').fadeIn().delay(900).fadeOut(300);
            setTimeout(() => $('#paste-image').remove(), 1200);
          }
          return;
        }
      });
    }

    // Fix hint popover position when the container is scrolled.
    // Summernote computes top/left relative to the document, but the popover has
    // position:fixed (viewport-relative). A MutationObserver fires as a microtask —
    // before the browser paints — so we can correct coords without any visible flicker.
    // Each SummernoteCorgee instance finds its own popover (the last one in the DOM,
    // since Summernote appends them to <body> in mount order).
    const allPopovers = document.querySelectorAll<HTMLElement>('.note-hint-popover');
    const hintPopoverEl = allPopovers.length > 0 ? allPopovers[allPopovers.length - 1] : null;

    let hintObserver: MutationObserver | null = null;
    if (hintPopoverEl)
    {
      hintObserver = new MutationObserver(() =>
      {
        if (!hintPopoverEl || hintPopoverEl.style.display === 'none') return;

        const sel = window.getSelection();
        if (!sel || sel.rangeCount === 0) return;

        const rect = sel.getRangeAt(0).getBoundingClientRect();
        if (!rect || (rect.width === 0 && rect.height === 0)) return;

        const popoverH = hintPopoverEl.offsetHeight || 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const topPos = spaceBelow > popoverH + 8 ? rect.bottom + 4 : rect.top - popoverH - 4;
        const newTop = `${topPos}px`;
        const newLeft = `${Math.min(rect.left, window.innerWidth - 270)}px`;

        if (hintPopoverEl.style.top === newTop && hintPopoverEl.style.left === newLeft) return;

        // Disconnect before mutating to avoid an infinite loop, then reconnect.
        hintObserver!.disconnect();
        hintPopoverEl.style.top = newTop;
        hintPopoverEl.style.left = newLeft;
        hintObserver!.observe(hintPopoverEl, { attributes: true, attributeFilter: ['style'] });
      });

      hintObserver.observe(hintPopoverEl, { attributes: true, attributeFilter: ['style'] });
    }

    // Watch for:
    //   1. New <ul>/<ol> being added (list created via toolbar) → propagate font-size
    //      from the spans inside each <li> to the <li> itself so the ::marker matches.
    //   2. Style attribute changes inside a list → adjust padding-left only.
    // Enter key adds <li> nodes, NOT <ul>/<ol>, so those are not caught by case 1.
    let listFontObserver: MutationObserver | null = null;
    const editableForList = corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined;
    if (editableForList)
    {
      listFontObserver = new MutationObserver(mutations =>
      {
        let needsPadding = false;
        for (const m of mutations)
        {
          if (m.type === 'childList')
          {
            m.addedNodes.forEach(node =>
            {
              if (!(node instanceof Element)) return;
              const lists: HTMLElement[] =
                node.tagName === 'UL' || node.tagName === 'OL'
                  ? [node as HTMLElement]
                  : [...node.querySelectorAll<HTMLElement>('ul, ol')];
              lists.forEach(list =>
              {
                // Use a CSS variable instead of li.style.fontSize so the ::marker
                // gets the right size without touching the li's font-size — setting
                // li.style.fontSize interferes with Summernote's Enter formatting.
                const firstSpan = list.querySelector<HTMLElement>('span[style*="font-size"]');
                const markerSize = firstSpan?.style.fontSize;
                if (markerSize) list.style.setProperty('--marker-font-size', markerSize);
              });
              if (lists.length > 0) needsPadding = true;
            });
          }
          if (m.type === 'attributes' && m.attributeName === 'style')
          {
            const t = m.target as Element;
            if (t.closest?.('li')) needsPadding = true;
          }
        }
        if (needsPadding) adjustListPadding(editableForList);
      });
      listFontObserver.observe(editableForList, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style'],
      });

      // Run once on init in case content already contains large font sizes.
      adjustListPadding(editableForList);

      // Prevent Enter on an empty bullet from exiting the list.
      // Lists only exit when the user explicitly deselects the toolbar button.
      const onKeydownForList = (e: KeyboardEvent) =>
      {
        if (e.key !== 'Enter') return;
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;
        const anchor = sel.getRangeAt(0).startContainer;
        const li = (anchor.nodeType === Node.TEXT_NODE
          ? anchor.parentElement
          : anchor as Element)?.closest('li');
        if (!li) return;
        if ((li.textContent ?? '').replace(/\u00a0/g, '').trim() !== '') return;

        e.preventDefault();
        e.stopImmediatePropagation();

        // Inherit the active font-size so the new bullet's text keeps the same size.
        const currentSpan = li.querySelector<HTMLElement>('span[style*="font-size"]');
        const fontSize = currentSpan?.style.fontSize || (li as HTMLElement).style.fontSize || '';

        const newLi = document.createElement('li');
        let cursorNode: Node;
        if (fontSize)
        {
          const span = document.createElement('span');
          span.style.fontSize = fontSize;
          const br = document.createElement('br');
          span.appendChild(br);
          newLi.appendChild(span);
          cursorNode = span;
        }
        else
        {
          const br = document.createElement('br');
          newLi.appendChild(br);
          cursorNode = newLi;
        }
        li.insertAdjacentElement('afterend', newLi);

        const newRange = document.createRange();
        newRange.setStart(cursorNode, 0);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      };
      editableForList.addEventListener('keydown', onKeydownForList, true);

      // Store for cleanup
      (editableForList as any).__listKeydownHandler = onKeydownForList;
    }

    return () =>
    {
      hintObserver?.disconnect();
      listFontObserver?.disconnect();
      if (editableForList)
      {
        const handler = (editableForList as any).__listKeydownHandler;
        if (handler) editableForList.removeEventListener('keydown', handler, true);
      }
      if (noteEditorEl)
      {
        noteEditorEl.removeEventListener('drop', handleDropCapture, true);
      }
      element.find('.note-editable').off('dragstart', onDragStart).off('dragend', onDragEnd);
      editableForCheckboxes.off('change', '.note-editor-checkbox', onCheckboxChange);
    };
  }, []);

  // Image selection: all four corners resize, Delete removes the image, and the
  // box shows on the first click even inside the edit modal. Content changes
  // are committed through Summernote (`editor.afterCommand` → `change`), which
  // already flows into onChangeSummernoteInternal, so no extra sync here.
  // Own effect with no early-return guard: the mount effect above bails out
  // when Summernote is still initializing and, with an empty dep array, never
  // runs again. Everything is resolved lazily per interaction instead.
  useEffect(() =>
  {
    return enableImageResizeHandles(
      () => corgeeSummernoteRef.current,
      () => corgeeSummernoteRef.current?.noteEditable?.[0] as HTMLElement | undefined,
    );
  }, []);

  localStorage.setItem('C2_isChatWindow', String(!!isInfoWindowChat));

  return (
    <div className={isAddReply ? 'corgee_com_card-add-replay-post-action something focused' : ''}>
      <ReactSummernoteCustom
        ref={corgeeSummernoteRef}
        defaultValue={initialBody}
        options={{
          height: 150,
          focus: true,
          dialogsInBody: true,
          disableLinkTarget: true,
          // Each undo snapshot copies the whole editor HTML, inline images included.
          historyLimit: 15,
          container: document.body,
          tooltip: false,
          disableDragAndDrop: CorgeeHelper.isSafari(),
          fontNames: [
            'Arial', 'Arial Black', 'Comic Sans MS', 'Courier New',
            'Helvetica', 'Impact', 'Roboto', 'Tahoma', 'Times New Roman', 'Verdana',
          ],
          // Roboto is a webfont; skip the installed-font check
          fontNamesIgnoreCheck: ['Roboto'],
          toolbar: toolbar,
          buttons: {
            AddCheckList: onButtonAddCheckList,
            AddWarningMessage: onButtonAddWarningMessage,
          },
          hint: [
            {
              conversationId: parseInt(String(conversationId)),
              mentions: [],
              match: /\B@([A-zÀ-ú0-9]*)$/,
              search: function (keyword: string, callback: (items: Person[]) => void)
              {
                keyword = keyword.toLowerCase();

                $('.core2').off();
                $('.corgee_com_card-body-info').off();
                $('.corgee_com_top-search-results-dash-container').off();

                setTimeout(() =>
                {
                  if ($('.note-hint-popover').length > 0)
                  {
                    $(
                      '.core2, .corgee_com_card-body-info, .corgee_com_top-search-results-dash-container',
                    ).on('scroll', () =>
                    {
                      $('.note-hint-popover').css('display', 'none');
                    });
                  }
                }, 100);

                const mentionsAvailable = getPeopleMentionsRef.current();
                const removeAccents = (str: string) =>
                  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

                callback(
                  mentionsAvailable.filter(
                    item =>
                      removeAccents(item.Name).toLowerCase().indexOf(removeAccents(keyword)) !== -1,
                  ),
                );
              },
              template: function (item: Person)
              {
                if (item.IsEnable)
                {
                  return item.Name;
                }
                else
                {
                  return `<div class="tag-not-available"><div>${item.Name}</div>(<span class="tag-not-available-text">This person does not have access to read the card</span>)<b conversation-id="${item.ConversationId}" person-id="${item.PersonId}" is-edit-body="true" class="add-layer-from-tag"> Add?</b></div>`;
                }
              },
              content: function (item: Person, _node: any)
              {
                if (!item.IsEnable) return '';

                let isGroup = false;
                let participantsId: string[] = [];
                let participantsName: string[] = [];

                groupNameRef.current.forEach(group =>
                {
                  if (group.NameGroup === item.Name)
                  {
                    isGroup = true;
                    participantsId = group.ParticipantsByID.split(',');
                    participantsName = group.ParticipantsByName.split(',');
                  }
                });

                if (isGroup)
                {
                  const spanParent = document.createElement('span');

                  if (corgeeSummernoteRef.current)
                  {
                    const element = $(corgeeSummernoteRef.current.editor).closest(
                      selectorContainer,
                    );
                    const elementNoteEditable = element.find('.note-editor');

                    participantsId.forEach((id, i) =>
                    {
                      const uniqueTaggedPeople = elementNoteEditable.find(`span[person-id=${id}]`);
                      if (uniqueTaggedPeople.length === 0)
                      {
                        const blueSpan = document.createElement('span');
                        $(blueSpan)
                          .append(participantsName[i]!)
                          .css('color', '#62AACB')
                          .attr('person-id', id);

                        const nextSpan = document.createElement('span');
                        $(nextSpan).append($(blueSpan)).append('&nbsp;&nbsp;');
                        $(spanParent).append($(nextSpan));
                      }
                    });
                  }

                  setTimeout(() => updateBodyByDOM(), 10);
                  return spanParent;
                }
                else
                {
                  const displayName = item.Name.replace(/^\(\d+\)\s*/, '');
                  const blueSpan = document.createElement('span');
                  $(blueSpan)
                    .append(displayName)
                    .css('color', '#62AACB')
                    .css('font-weight', '600')
                    .attr('person-id', item.PersonId);

                  const nextSpan = document.createElement('span');
                  $(nextSpan).append($(blueSpan)).append('&nbsp;&nbsp;');

                  setTimeout(() => updateBodyByDOM(), 10);
                  return nextSpan;
                }
              },
            },
            {
              conversationId: parseInt(String(conversationId)),
              mentions: [],
              match: /\B\[\[(\w*)$/,
              search: function (keyword: string, callback: (items: Card[]) => void)
              {
                keyword = keyword.toLowerCase();

                $('.core2').off();
                $('.corgee_com_card-body-info').off();
                $('.corgee_com_top-search-results-dash-container').off();

                setTimeout(() =>
                {
                  if ($('.note-hint-popover').length > 0)
                  {
                    $(
                      '.core2, .corgee_com_card-body-info, .corgee_com_top-search-results-dash-container',
                    ).on('scroll', () =>
                    {
                      $('.note-hint-popover').css('display', 'none');
                    });
                  }
                }, 100);

                const cardsAvailable = getCardsLinkedRef.current();
                callback(
                  cardsAvailable.filter(item =>
                  {
                    const complete_card_name = `${item.Title} - ${item.ProjectName}`;
                    return complete_card_name.toLowerCase().indexOf(keyword) !== -1;
                  }),
                );
              },
              template: function (item: Card)
              {
                return item.ProjectName ? `${item.Title} - ${item.ProjectName}` : `${item.Title}`;
              },
              content: function (item: Card, _node: any)
              {
                const url = buildConversationDetailsUrl(item.ConversationId);

                const label = item.ProjectName
                  ? `${item.Title} - ${item.ProjectName}`
                  : String(item.Title ?? '');

                const blueAtag = document.createElement('a');
                $(blueAtag)
                  .append('[[', label, ']]')
                  .attr('href', url)
                  .attr('target', '_blank')
                  .attr('link-conversation-id', String(item.ConversationId));

                const nextSpan = document.createElement('span');
                $(nextSpan).append($(blueAtag)).append('&nbsp;');

                setTimeout(() => updateBodyByDOM(), 10);
                return nextSpan;
              },
            },
          ],
        }}
        onChange={onChangeSummernoteInternal}
        onInit={onInit}
        onPaste={onPaste}
        onBlur={flushBodyToHost}
        onKeyDown={handleKeyDown}
        onImageUpload={onImageUploadFix}
      />
      {children}
    </div>
  );
});

SummernoteCorgee.displayName = 'SummernoteCorgee';

export default SummernoteCorgee;
